import { Resources } from 'constants/resources';

import { useCallback, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { ReconciliationFileInput } from 'pages/Finance/Reconciliation/components/ReconciliationFileInput';
import { ReconciliationMerchantInput } from 'pages/Finance/Reconciliation/components/ReconciliationMerchantInput';
import { ReconciliationProviderInput } from 'pages/Finance/Reconciliation/components/ReconciliationProviderInput';
import { useReconciliationJobNotification } from 'pages/Finance/Reconciliation/hooks/useReconciliationJobNotification';
import { useReconciliationSubmit } from 'pages/Finance/Reconciliation/hooks/useReconciliationSubmit';
import { validationSchema } from 'pages/Finance/Reconciliation/schemas/schema';
import { getReconciliationErrorMessage } from 'pages/Finance/Reconciliation/utils/getReconciliationErrorMessage';
import { ReconciliationFormValues } from 'pages/Finance/Reconciliation/utils/mapReconciliationFormToFormData';
import { Controller, FormProvider, Resolver, useForm } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Breadcrumbs } from 'shared/mui/Breadcrumbs';
import { Button } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { DateTimeRangeTimezonePicker } from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { Typography } from 'shared/mui/Typography';
import { EnumAutocompleteInput } from 'shared/react-admin/Inputs/EnumAutocompleteInput/EnumAutocompleteInput';

import {
  ReconciliationFormFieldsStyled,
  ReconciliationFormFilesStyled,
} from './ReconciliationForm.styled';

const DEFAULT_VALUES: ReconciliationFormValues = {
  period: [null, null],
  timezone: null,
  merchant: null,
  provider: null,
  direction: null,
  providerFile: null,
  merchantFile: null,
  providerFileTimezone: null,
  merchantFileTimezone: null,
};

export const ReconciliationForm: React.FC = () => {
  const form = useForm<ReconciliationFormValues>({
    resolver: yupResolver(validationSchema) as unknown as Resolver<ReconciliationFormValues>,
    mode: 'all',
    defaultValues: DEFAULT_VALUES,
  });

  const {
    mutate: reconcile,
    data: job,
    error,
    isPending,
    isSuccess,
    reset: resetResult,
  } = useReconciliationSubmit();
  const isLocked = isPending || isSuccess;
  const { isViewed } = useReconciliationJobNotification(job?.jobId);

  const onClear = useCallback(() => {
    form.reset(DEFAULT_VALUES);
    resetResult();
  }, [form, resetResult]);

  useEffect(() => {
    if (isViewed) {
      onClear();
    }
  }, [isViewed, onClear]);

  return (
    <Box>
      <Breadcrumbs sx={{ marginY: '24px' }}>
        <Typography color="primary">Финансы</Typography>
        <Typography color="text.primary">Автосверка</Typography>
      </Breadcrumbs>
      <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardHeader title="Автосверка" titleSize="smallBold" />
        <FormProvider {...form}>
          <form noValidate onSubmit={form.handleSubmit((values) => reconcile(values))}>
            <CardContent>
              <ReconciliationFormFieldsStyled>
                <Controller
                  control={form.control}
                  name="period"
                  render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                    <DateTimeRangeTimezonePicker
                      disabled={isLocked}
                      error={!!error}
                      helperText={error?.message}
                      label="Период от–до"
                      onBlur={onBlur}
                      onChange={onChange}
                      onChangeTimezone={(timezone) => form.setValue('timezone', timezone)}
                      required
                      timezone={form.watch('timezone')}
                      value={value}
                    />
                  )}
                />
                <ReconciliationMerchantInput readOnly={isLocked} />
                <ReconciliationProviderInput readOnly={isLocked} />
                <EnumAutocompleteInput
                  fullWidth
                  isRequired
                  label="Направление"
                  optionText="description"
                  optionValue="value"
                  readOnly={isLocked}
                  resource={Resources.Directions}
                  source="direction"
                  variant="outlined"
                />
              </ReconciliationFormFieldsStyled>
              <Alert severity="info" sx={{ marginBottom: '24px' }}>
                Формат XLSX, не более 25 МБ, не более 100 000 строк.
              </Alert>
              <ReconciliationFormFilesStyled>
                <ReconciliationFileInput
                  disabled={isLocked}
                  label="Файл провайдера"
                  name="providerFile"
                  timezoneName="providerFileTimezone"
                />
                <ReconciliationFileInput
                  disabled={isLocked}
                  label="Файл мерчанта"
                  name="merchantFile"
                  timezoneName="merchantFileTimezone"
                />
              </ReconciliationFormFilesStyled>
              {error && <Alert severity="error">{getReconciliationErrorMessage(error)}</Alert>}
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <Button
                  disabled={isPending}
                  key="clear-button"
                  label="Очистить"
                  onClick={onClear}
                  variant="outlined"
                />,
                <Button
                  disabled={isLocked || !form.formState.isValid}
                  isLoading={isPending}
                  key="submit-button"
                  label="Сделать сверку"
                  type="submit"
                  variant="contained"
                />,
              ]}
            />
          </form>
        </FormProvider>
      </Card>
    </Box>
  );
};
